import { createContext, useContext } from 'react'

/**
 * Everything the AI listing flow is holding on to.
 *
 * Files live here rather than in the intake panel because they outlive it. A
 * photograph dropped on this page cannot be uploaded yet — media uploads need
 * a listing ID, and the listing does not exist until the wizard is submitted —
 * so the browser keeps the File objects until each listing has been created
 * and can receive them.
 *
 * That also means the agent never uploads the same photo twice: once for the
 * AI to read, once for the gallery.
 */
export type HeldFileKind = 'document' | 'image' | 'video' | 'audio'

export interface HeldFile {
    id: string
    file: File
    kind: HeldFileKind
    /** Object URL for images, revoked when the file is dropped. */
    preview: string | null
    /** Added in the review for a listing's gallery: not for the AI to read, not shown in the chat box. */
    galleryOnly?: boolean
}

export type IntakeFields = Record<string, string | number | null>

export type IntakeConfidence = 'high' | 'medium' | 'low'

/** Names the model chose, resolved by the API to the IDs the wizard selects. */
export interface IntakeCategory {
    listingType: string | null
    propertyType: string | null
    propertySubType: string | null
    confidence: IntakeConfidence
    reason: string
    ids: {
        listingTypeId: number | null
        propertyTypeId: number | null
        propertySubTypeId: number | null
        tenureId: number | null
        titleTypeId: number | null
        forSaleOrRentId: number | null
        projectTypeId: number | null
        propertyUnitTypeId: number | null
        furnishingConditionId: number | null
    }
}

/**
 * Where the price came from. "stated" is a figure in the sources; "estimated"
 * is the model's guide figure from size and location, which fills the form
 * but is labelled until the agent confirms it; "unknown" leaves it empty.
 */
export interface IntakePricing {
    status: 'stated' | 'estimated' | 'unknown'
    price: number | null
    low: number | null
    high: number | null
    currency: string
    perUnitRate: number | null
    perUnitLabel: string | null
    basis: string
    assumptions: string[]
    confidence: IntakeConfidence
}

/**
 * What JUPEM MyLot showed for this land, looked up by the API the way an
 * agent does by hand. 'lot' is the land's own lot found; 'adjoining' is a
 * lot the survey has for a site the plan left unnumbered; 'within' is the
 * larger lot the plan's land is a part of; 'near' is a site
 * with no lot of its own, placed by the numbered lots around it.
 */
export interface LotLookup {
    /** The land the lookup was for; a lookup is carried over while this stays the same. */
    key?: string
    source: string
    status: 'lot' | 'adjoining' | 'within' | 'near' | 'not_found' | 'unavailable'
    reason?: string | null
    lot?: string | null
    pa?: string | null
    /** The size of the lot found, which for 'within' is the larger lot the land is part of. */
    lotAreaSqm?: number | null
    searched: { lot: string; pa?: string | null }[]
    shownLot?: string | null
    location: { lat: number; lng: number } | null
    /** The map of the lot among its neighbours: MyLot's own page, or drawn from its boundaries. */
    imageUrl: string | null
    /** What went wrong, for whoever looks into a lookup that could not run. */
    detail?: string | null
    /** The API could not reach MyLot and left the lookup for this browser (see myLotLookup.ts). */
    browser?: boolean
}

export interface IntakeDraft {
    draftId?: string
    fields: IntakeFields
    /** What the sources establish about this property, for the agent. */
    summary: string
    category: IntakeCategory
    pricing: IntakePricing
    sourceReferences: string[]
    missingFields: string[]
    questions: string[]
    /** Set once Google has been asked to fill this draft's location gaps, so it is asked once. */
    geocoded?: boolean
    /** Location fields Google filled rather than the files or the agent. */
    googleFilled?: string[]
    lotLookup?: LotLookup
}

/** Who stands behind a document, from the strongest authority down. */
export type IntakeIssuer = 'government' | 'licensed_professional' | 'developer' | 'agent_or_owner' | 'unknown'

export interface IntakeSource {
    file: string
    documentType: string
    description: string
    /** Who issued it; absent on sessions saved before the AI reported this. */
    issuer?: IntakeIssuer
    /** True when an authority stands behind it and it identifies the property: what the form accepts as proof of ownership. */
    proofOfOwnership?: boolean
    /** Why it counts as proof: the issuer and the identifier that ties it to the property. */
    proofReason?: string | null
    /**
     * Whether a buyer should see this image in the listing's gallery: a
     * photograph of the property or a clean floor plan. False for documents,
     * however they were uploaded: a title, a survey or pre-computation plan,
     * a bill. Absent on sessions saved before the AI reported this.
     */
    displayable?: boolean
    /** For a voice note: what the AI heard, in the languages spoken, so a mishearing can be corrected. */
    transcript: string | null
}

/** One turn of the conversation, as the API saved it. */
export interface IntakeMessage {
    id: number
    role: 'user' | 'assistant'
    content: string
    files: { name: string; kind: string; hash?: string; mimetype?: string }[]
    createdAt: string
}

/** The saved session this page is working in. */
export interface IntakeSessionRef {
    publicId: string
    title: string
    completed?: number[]
    listings?: { draftIndex: number; listingId: number }[]
}

export interface IntakeSessionSummary {
    ignored?: boolean
    publicId: string
    title: string
    status: 'active' | 'archived'
    draftCount: number
    completedCount: number
    files: { name: string; kind: string; hash?: string; mimetype?: string }[]
    messageCount: number
    lastMessageAt: string | null
    createdAt: string
}

export interface IntakeSessionDetail extends IntakeSessionSummary {
    rejectedUpload?: boolean
    drafts: IntakeDraft[]
    sources: IntakeSource[]
    completed: number[]
    listings: { draftIndex: number; listingId: number }[]
    messages: IntakeMessage[]
}

/** A listing needs a gallery, and the gallery step will not accept fewer. */
export const MIN_IMAGES_PER_LISTING = 5
export const MAX_HELD_FILES = 40
/** What one extraction request may carry; the API enforces the same ceiling. */
export const MAX_FILES_PER_REQUEST = 20
export const MAX_LISTINGS = 10
export const MAX_FILE_BYTES = 10 * 1024 * 1024
export const MAX_AUDIO_BYTES = 25 * 1024 * 1024
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
export const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm']
export const AUDIO_TYPES = [
    'audio/mpeg',
    'audio/mp3',
    'audio/mp4',
    'audio/m4a',
    'audio/x-m4a',
    'audio/aac',
    'audio/wav',
    'audio/x-wav',
    'audio/wave',
    'audio/webm',
    'audio/ogg',
    'audio/flac',
]
export const DOCUMENT_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/csv',
    'text/plain',
]
export const ACCEPT = '.pdf,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp,.mp4,.mov,.webm,.mp3,.m4a,.wav,.ogg,.aac'

export const kindOf = (file: File): HeldFileKind | null => {
    const type = file.type.split(';')[0]
    if (IMAGE_TYPES.includes(type)) return 'image'
    if (VIDEO_TYPES.includes(type)) return 'video'
    if (AUDIO_TYPES.includes(type)) return 'audio'
    if (DOCUMENT_TYPES.includes(type)) return 'document'
    // Some browsers leave type empty for .xlsx dragged from certain apps, and
    // for recordings saved by other tools.
    if (!type && /\.(jpg|jpeg|png|webp)$/i.test(file.name)) return 'image'
    if (!type && /\.(mp4|mov|webm)$/i.test(file.name)) return 'video'
    if (!type && /\.(xlsx|csv|pdf|txt)$/i.test(file.name)) return 'document'
    if (!type && /\.(mp3|m4a|wav|ogg|aac|webm)$/i.test(file.name)) return 'audio'
    return null
}

export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
    land_title: 'Land title',
    strata_title: 'Strata title',
    title_search: 'Title search',
    title_plan: 'Title plan',
    survey_plan: 'Survey or site plan',
    quit_rent_bill: 'Quit rent bill',
    assessment_bill: 'Assessment bill',
    sale_and_purchase_agreement: 'Sale and purchase agreement',
    valuation_report: 'Valuation report',
    official_letter: 'Official letter',
    utility_bill: 'Utility bill',
    brochure: 'Brochure',
    floor_plan: 'Floor plan',
    tenancy_agreement: 'Tenancy agreement',
    spreadsheet: 'Spreadsheet',
    photo: 'Photo',
    voice_note: 'Voice note',
    notes: 'Your notes',
    other: 'Document',
}

/** Kinds of upload that are evidence, not pictures of the property, on sessions that predate `displayable`. */
const DOCUMENT_KINDS = new Set(['land_title', 'strata_title', 'title_search', 'title_plan', 'survey_plan', 'quit_rent_bill', 'assessment_bill', 'sale_and_purchase_agreement', 'valuation_report', 'official_letter', 'utility_bill', 'tenancy_agreement', 'spreadsheet', 'irrelevant'])

/**
 * Whether an uploaded image belongs in a listing's gallery. An image the AI
 * was never shown (one added in the review) is the agent's own choice and
 * stays; one the AI read is kept out when it is a document.
 */
export const isGalleryImage = (fileName: string, sources: IntakeSource[]): boolean => {
    const source = sources.find((s) => s.file === fileName)
    if (!source) return true
    return source.displayable === undefined ? !DOCUMENT_KINDS.has(source.documentType) : source.displayable
}

export const ISSUER_LABELS: Record<IntakeIssuer, string> = {
    government: 'Government-issued',
    licensed_professional: 'From a licensed professional',
    developer: 'From the developer',
    agent_or_owner: 'Your own material',
    unknown: 'Issuer not known',
}

export const formatMYR = (value: number) =>
    new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR', maximumFractionDigits: 0 }).format(value)

export interface AIIntakeValue {
    files: HeldFile[]
    drafts: IntakeDraft[]
    /** What the last extraction said each uploaded file was. */
    sources: IntakeSource[]
    /** The conversation so far, from the saved session. */
    messages: IntakeMessage[]
    /** The saved session, once the first turn has been answered. */
    session: IntakeSessionRef | null
    /** Draft index -> the listing it became. */
    created: { draftIndex: number; listingId: number }[]
    /** Draft index -> held file ids making up that listing's gallery. */
    assignments: Record<number, string[]>
    /** Draft index -> the ownership proof for it, which the form insists on. */
    proofs: Record<number, File>
    /** Draft indexes already created as real listings. */
    completed: number[]
    /** The draft currently loaded into the wizard, if any. */
    active: number | null

    addFiles: (incoming: File[], assignTo?: number) => string[]
    removeFile: (id: string) => void
    clearFiles: () => void
    setDrafts: (next: IntakeDraft[], sources?: IntakeSource[], session?: IntakeSessionRef | null) => void
    hydrate: (detail: IntakeSessionDetail) => void
    appendMessages: (next: IntakeMessage[]) => void
    /** The agent's own edit to one draft, from the review; saved with the session. */
    updateDraft: (draftIndex: number, change: (draft: IntakeDraft) => IntakeDraft) => void
    setProof: (draftIndex: number, file: File | null) => void
    toggleAssignment: (draftIndex: number, fileId: string) => void
    setActive: (draftIndex: number | null) => void
    markCompleted: (draftIndex: number, listingId?: number) => void
    reset: () => void

    /** The media held for one draft, in the order the agent added them. */
    mediaFor: (draftIndex: number) => HeldFile[]
    imageCountFor: (draftIndex: number) => number
}

export const AIIntakeContext = createContext<AIIntakeValue | null>(null)

export const useAIIntake = () => {
    const value = useContext(AIIntakeContext)
    if (!value) throw new Error('useAIIntake must be used inside an AIIntakeProvider.')
    return value
}
